import type {
  CompetitionLink,
  EventTypeLink,
  MeetingLink,
  RunnerLink,
  SearchEventLink
} from '@ps/isp-web-services-types';
import { type Alias, select } from '@ps/web-core-store';
import { Link } from '@ps/web-shared-components';
import { capitalize } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';

import { TitleWithHighlight } from '@swf/sports/components/TitleWithHighlight/TitleWithHighlight';

import { useSportsMenuWidget } from '../../../../model';
import { updatePreviousSearchResults } from '../PreviousSearchResults/utils/update-previous-search-results';

import styles from './SearchResult.scss';

interface Props {
  alias: Alias;
  link: EventTypeLink | RunnerLink | CompetitionLink | MeetingLink | SearchEventLink;
}

const shouldShowLinkDate = (
  link: Props['link']
): link is SearchEventLink | MeetingLink | RunnerLink => 'openDate' in link;

function getEventDateFormatted(dateStr: string, locale: string) {
  const date = new Date(dateStr);
  const dayOfWeek = new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(date);
  const day = date.getDate();
  const time = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(date);
  const month = new Intl.DateTimeFormat(locale, { month: 'short' }).format(date);

  return <time dateTime={dateStr}> {`${time} - ${dayOfWeek} ${day} ${month}`}</time>;
}

const SearchResult: React.FC<Props> = ({ alias, link }) => {
  const { widgetDispatch, widgetSelect } = useSportsMenuWidget();
  const searchQuery = useSelector(widgetSelect.searchQuery);
  const locale = useSelector(select.locale.get);
  let linkText: string = '';

  switch (true) {
    case 'text' in link:
      linkText = link.text;
      break;
    case 'runnerName' in link:
      linkText = link.runnerName;
      break;
    case 'venue' in link:
      linkText = link.venue;
      break;
    default:
      break;
  }

  const searchResultTextWithHighlight = (
    <TitleWithHighlight title={linkText} partToHighlight={searchQuery} />
  );

  const handleSearchResultClick = () => {
    const capitalizedQuery = capitalize(searchQuery);
    const updatedPrevSearchResults = updatePreviousSearchResults(capitalizedQuery);

    widgetDispatch.updatePreviousSearchResults(updatedPrevSearchResults);
    widgetDispatch.togglePreviousSearchResults(false);
    widgetDispatch.updateSearchQuery('');
  };

  return (
    <li className={styles.container}>
      <Link
        className={styles.searchResult}
        alias={alias}
        values={{ ...link }}
        onClick={handleSearchResultClick}
        trackText={locale === 'en' ? linkText : undefined}
      >
        {searchResultTextWithHighlight}
        {shouldShowLinkDate(link) ? (
          <span className={styles.secondaryInfo}>
            {getEventDateFormatted(link.openDate, locale)}{' '}
            {'competitionName' in link && `- ${link.competitionName}`}
            {'runnerName' in link && `- ${link.venue}`}
          </span>
        ) : null}
      </Link>
    </li>
  );
};

export default SearchResult;
